import { useState, useEffect } from 'react';
import { useShift } from '../contexts/ShiftContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  Clock,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  Square,
  Calculator,
} from 'lucide-react';

export function ShiftIndicator() {
  const { currentShift, hasActiveShift, loading } = useShift();
  const { formatCurrency } = useCurrency();
  const [openShiftModal, setOpenShiftModal] = useState(false);
  const [closeShiftModal, setCloseShiftModal] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading...</span>
      </div>
    );
  }

  return (
    <>
      {hasActiveShift ? (
        <button
          onClick={() => setCloseShiftModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors"
          data-testid="shift-indicator-open"
        >
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-sm text-green-500 font-medium">
            Shift Active
          </span>
          <Badge variant="outline" className="text-xs">
            {formatCurrency(currentShift?.opening_cash)}
          </Badge>
        </button>
      ) : (
        <button
          onClick={() => setOpenShiftModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 transition-colors"
          data-testid="shift-indicator-closed"
        >
          <div className="w-2 h-2 rounded-full bg-red-500" />
          <span className="text-sm text-red-500 font-medium">
            No Active Shift
          </span>
        </button>
      )}

      <OpenShiftModal open={openShiftModal} onOpenChange={setOpenShiftModal} />
      <CloseShiftModal open={closeShiftModal} onOpenChange={setCloseShiftModal} />
    </>
  );
}

export function OpenShiftModal({ open, onOpenChange }) {
  const { openShift, loading } = useShift();
  const [openingCash, setOpeningCash] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!openingCash || parseFloat(openingCash) < 0) {
      toast.error('Please enter a valid opening cash amount');
      return;
    }

    setSubmitting(true);
    try {
      await openShift(openingCash, notes);
      toast.success('Shift opened successfully');
      onOpenChange(false);
      setOpeningCash('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open shift');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="open-shift-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-green-500" />
            Open New Shift
          </DialogTitle>
          <DialogDescription>
            Count your cash drawer and enter the opening amount to start your shift.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="opening-cash">Opening Cash (Rp) *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="opening-cash"
                type="number"
                placeholder="0"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
                className="pl-10 text-lg font-mono"
                data-testid="opening-cash-input"
              />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Count all cash in the drawer before starting
            </p>
          </div>

          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any notes about the opening..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1"
              rows={2}
              data-testid="shift-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !openingCash}
            data-testid="open-shift-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Open Shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CloseShiftModal({ open, onOpenChange }) {
  const { currentShift, closeShift, getShiftSummary, loading } = useShift();
  const { formatCurrency } = useCurrency();
  const [summary, setSummary] = useState(null);
  const [closingCash, setClosingCash] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);

  useEffect(() => {
    if (open && currentShift) {
      loadSummary();
    }
  }, [open, currentShift]);

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const data = await getShiftSummary();
      setSummary(data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const expectedCash = summary?.expected_cash || 0;
  const actualCash = parseFloat(closingCash) || 0;
  const difference = actualCash - expectedCash;
  const hasDifference = closingCash && Math.abs(difference) > 0.01;

  const handleSubmit = async () => {
    if (!closingCash || parseFloat(closingCash) < 0) {
      toast.error('Please enter the actual cash amount');
      return;
    }

    if (hasDifference && !reason.trim()) {
      toast.error('Please provide a reason for the cash difference');
      return;
    }

    setSubmitting(true);
    try {
      const result = await closeShift(closingCash, reason || null);
      toast.success('Shift closed successfully');
      onOpenChange(false);
      setClosingCash('');
      setReason('');
      setSummary(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close shift');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="close-shift-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Square className="h-5 w-5 text-red-500" />
            Close Shift
          </DialogTitle>
          <DialogDescription>
            Count your cash drawer and reconcile with system records.
          </DialogDescription>
        </DialogHeader>

        {loadingSummary ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Shift Summary */}
            <div className="bg-muted rounded-lg p-4 space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Cash Calculation
              </h4>
              
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Opening Cash</span>
                  <span className="font-mono">{formatCurrency(summary?.opening_cash)}</span>
                </div>
                <div className="flex justify-between text-green-500">
                  <span>+ Cash Sales</span>
                  <span className="font-mono">{formatCurrency(summary?.cash_sales)}</span>
                </div>
                <div className="flex justify-between text-red-500">
                  <span>- Cash Refunds</span>
                  <span className="font-mono">{formatCurrency(summary?.cash_refunds)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Expected Cash</span>
                  <span className="font-mono text-lg">{formatCurrency(expectedCash)}</span>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-muted rounded-lg p-2">
                <p className="text-lg font-bold">{summary?.orders_count || 0}</p>
                <p className="text-xs text-muted-foreground">Orders</p>
              </div>
              <div className="bg-muted rounded-lg p-2">
                <p className="text-lg font-bold">{summary?.transactions_count || 0}</p>
                <p className="text-xs text-muted-foreground">Transactions</p>
              </div>
              <div className="bg-muted rounded-lg p-2">
                <p className="text-lg font-bold">{formatCurrency(summary?.total_sales)}</p>
                <p className="text-xs text-muted-foreground">Total Sales</p>
              </div>
            </div>

            {/* Actual Cash Input */}
            <div>
              <Label htmlFor="closing-cash">Actual Cash in Drawer (Rp) *</Label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="closing-cash"
                  type="number"
                  placeholder="0"
                  value={closingCash}
                  onChange={(e) => setClosingCash(e.target.value)}
                  className="pl-10 text-lg font-mono"
                  data-testid="closing-cash-input"
                />
              </div>
            </div>

            {/* Difference Display */}
            {closingCash && (
              <div className={`p-3 rounded-lg flex items-center justify-between ${
                Math.abs(difference) < 0.01 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-yellow-500/10 border border-yellow-500/30'
              }`}>
                <div className="flex items-center gap-2">
                  {Math.abs(difference) < 0.01 ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  )}
                  <span className="font-medium">
                    {Math.abs(difference) < 0.01 ? 'Cash Balanced!' : 'Cash Difference'}
                  </span>
                </div>
                <span className={`font-mono font-bold ${
                  difference < 0 ? 'text-red-500' : difference > 0 ? 'text-green-500' : ''
                }`}>
                  {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                </span>
              </div>
            )}

            {/* Reason Input (required if difference) */}
            {hasDifference && (
              <div>
                <Label htmlFor="reason" className="text-yellow-500">
                  Reason for Difference *
                </Label>
                <Textarea
                  id="reason"
                  placeholder="Explain the cash difference..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1"
                  rows={2}
                  data-testid="difference-reason-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Examples: Kurang kembalian, Salah input pembayaran, Laci terbuka
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !closingCash || (hasDifference && !reason.trim())}
            variant={hasDifference ? 'destructive' : 'default'}
            data-testid="close-shift-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Square className="h-4 w-4 mr-2" />
            )}
            Close Shift
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Hook to check shift before actions
export function useShiftGuard() {
  const { hasActiveShift, currentShift } = useShift();
  const [showOpenModal, setShowOpenModal] = useState(false);

  const requireShift = (callback) => {
    if (!hasActiveShift) {
      setShowOpenModal(true);
      return false;
    }
    callback();
    return true;
  };

  return {
    hasActiveShift,
    currentShift,
    requireShift,
    OpenShiftPrompt: () => (
      <OpenShiftModal open={showOpenModal} onOpenChange={setShowOpenModal} />
    )
  };
}
